/**
 * Site visits (`docs/plans/site-visits.md`) — the app's half.
 *
 * The page view itself is `public/visit.js`, one script for every page we serve,
 * signed in or not. What only the app can do is say *who* this browser is, once
 * someone signs in: `POST /visit/claim` stamps the account on this browser's
 * anonymous views, which is what joins a visit to a sign-up. The visitor id is
 * the usage tracker's `deviceId()` — the same `qf.deviceId` `visit.js` keeps.
 *
 * Every signed-in page load, so the views made while signed in are claimed too.
 * Never throws: a lost claim is caught by the next load.
 */

import { deviceId } from "../lib/activity";
import { currentUserId, requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL as string | undefined);

export async function claimVisits(): Promise<void> {
  if (!BASE) return;
  try {
    await fetch(`${BASE}/visit/claim?userId=${encodeURIComponent(currentUserId())}`, {
      method: "POST",
      headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify({ visitorId: deviceId() }),
    });
  } catch {
    /* the next load claims it */
  }
}

// ── the report: ☰ Admin › Usage › Visitors ──────────────────────────────────

export interface VisitCount {
  views: number;
  visitors: number;
}

export interface VisitReport {
  days: number;
  brand: string | null;
  /** Every brand seen in the window, for the picker. */
  brands: string[];
  views: number;
  visitors: number;
  /** Accounts created in the window, test accounts left out. */
  signups: number;
  /** Of those, the ones whose browser sent a view. */
  signupsSeen: number;
  /** Of those sign-ups, the ones now paying. */
  paid: number;
  /** UTC days. */
  byDay: (VisitCount & { day: string })[];
  countries: (VisitCount & { country: string | null })[];
  cities: (VisitCount & { city: string; country: string | null })[];
  referrers: (VisitCount & { referrer: string | null })[];
  pages: (VisitCount & { host: string; path: string })[];
  devices: (VisitCount & { deviceType: string | null })[];
  campaigns: (VisitCount & { campaign: string; source: string | null; signups: number })[];
}

/** Throws, unlike the claim: an empty report must not look like an outage. */
export async function fetchVisitReport(days: number, brand: string | null): Promise<VisitReport> {
  if (!BASE) throw new Error("no server configured");
  const params = new URLSearchParams({ days: String(days), userId: currentUserId() });
  if (brand) params.set("brand", brand);
  const response = await fetch(`${BASE}/admin/visits?${params}`, { headers: await requestHeaders() });
  if (!response.ok) throw new Error((await response.text()) || `GET /admin/visits failed (${response.status})`);
  return (await response.json()) as VisitReport;
}
